import { useState } from "react";

import { ColumnType } from "../../../../../../common/components/xepp/table/DataTable";
import { Money } from "../../../../../../model/common/money";
import { ReconcilliationResult, ReconcilliationTransactionDetail } from "../../../../../../model/reconcilliation/reconcilliation";
import { VendingStatusMap, vendingStatusClassName } from "../../../../../../model/transaction";
import { useGetReconcilliationTransactionDetailsQuery } from "../../../../../../service/reconcilliation-service";
import { formatTimestamp } from "../../../../../../utils";
import { ExceptionTransactionsTable, PAGE_SIZE } from "../exception-transactions-table/ExceptionTransactionsTable";
import statusStyles from "../../../../utilityAdmin.module.scss";
import styles from "./TransactionsDetails.module.scss";

const money = (amount: Money): string => new Money(amount).toLocaleString();

const timestamp = (value?: string | null): string => (value ? formatTimestamp(value) : "N/A");

/**
 * Whole days from the purchase transaction to the moment the vend token completed, the same two
 * instants the job averages — so a row's lag and the section's average always agree.
 */
const lagInDays = (row: ReconcilliationTransactionDetail): string => {
	if (!row.vendedAt) return "N/A";

	const days = Math.floor((new Date(row.vendedAt).getTime() - new Date(row.createdAt).getTime()) / 86_400_000);
	return days === 1 ? "1 Day" : `${days} Days`;
};

const referenceColumns: ColumnType<ReconcilliationTransactionDetail>[] = [
	{ header: "XEPP Reference", accessor: "reference", render: row => row.reference || "N/A" },
	{ header: "Payment Hub Reference", accessor: "externalReference", render: row => row.externalReference ?? "N/A" },
];

const refundedColumns: ColumnType<ReconcilliationTransactionDetail>[] = [
	...referenceColumns,
	{ header: "Vending Reference", accessor: "vendingTransactionId", render: row => row.vendingTransactionId ?? "N/A" },
	{ header: "Amount", accessor: "amount", render: row => money(row.amount) },
	{ header: "Vended At", accessor: "vendedAt", render: row => timestamp(row.vendedAt) },
	{ header: "Refunded At", accessor: "refundedAt", render: row => timestamp(row.refundedAt) },
	{
		header: "Customer / Meter",
		accessor: "meterNumber",
		render: row => row.meterNumber || "N/A",
	},
];

/**
 * The job buckets an obligation by the whole days between purchase and the end of the reconcilled
 * day, so age is measured against that same instant — WAT, like every other figure on the report.
 * Measuring against today instead would age a stuck row every time the report is reopened.
 */
const daysStuck = (createdAt: string, reportDate: string): string => {
	const dayEnd = new Date(`${reportDate}T00:00:00+01:00`).getTime() + 86_400_000;

	const days = Math.floor((dayEnd - new Date(createdAt).getTime()) / 86_400_000);
	return days === 1 ? "1 Day" : `${days} Days`;
};

const lateCompletionColumns: ColumnType<ReconcilliationTransactionDetail>[] = [
	...referenceColumns,
	{ header: "Created", accessor: "createdAt", render: row => timestamp(row.createdAt) },
	{ header: "Completed", accessor: "vendedAt", render: row => timestamp(row.vendedAt) },
	{ header: "Lag", accessor: "lag", render: lagInDays },
	{ header: "Amount", accessor: "amount", render: row => money(row.amount) },
	/*
	{
		header: "Vended",
		accessor: "vended",
		render: row => (
			<span className={row.vendedAt ? styles.vended : styles.notVended}>{row.vendedAt ? "Yes" : "No"}</span>
		),
	},
	*/
];

const stuckBacklogColumns = (reportDate: string): ColumnType<ReconcilliationTransactionDetail>[] => [
	...referenceColumns,
	{ header: "Settled", accessor: "createdAt", render: row => timestamp(row.createdAt) },
	{ header: "Age", accessor: "age", render: row => daysStuck(row.createdAt, reportDate) },
	{ header: "Amount", accessor: "amount", render: row => money(row.amount) },
	{ header: "Last Attempt", accessor: "lastAttemptAt", render: row => timestamp(row.lastAttemptAt) },
	{
		header: "Status",
		accessor: "lastAttemptStatus",
		// The dot-and-label indicator the vending history table uses for the same enum. N/A
		// rather than "Unknown" when there is no attempt at all: on a bill, or on a vend paid for
		// but never once attempted — Unknown is itself a real VendingStatus.
		render: row =>
			row.lastAttemptStatus === null || row.lastAttemptStatus === undefined ? (
				"N/A"
			) : (
				<span
					className={`${statusStyles.statusIndicator} ${statusStyles[vendingStatusClassName(row.lastAttemptStatus)] ?? ""}`}
				>
					{VendingStatusMap[row.lastAttemptStatus] ?? "Unknown"}
				</span>
			),
	},
];

/** Only the current page's ids go to the server — a section can hold thousands. */
const pageOf = (transactionIds: string[], page: number): string[] =>
	transactionIds.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

interface TransactionsDetailsProps {
	result: ReconcilliationResult;
}

export const TransactionsDetails = ({ result }: Readonly<TransactionsDetailsProps>) => {
	const { refundedAfterDelivery, lateCompletions } = result.exceptions;
	const stuckTransactionIds = result.backlogAging.fourPlusDaysTransactionIds;

	const [refundedPage, setRefundedPage] = useState(1);
	const [latePage, setLatePage] = useState(1);
	const [stuckPage, setStuckPage] = useState(1);

	// The three tables are shown together, so their pages are read in one request. Paging any one
	// of them refetches all three.
	const request = {
		refundedTransactionIds: pageOf(refundedAfterDelivery.transactionIds, refundedPage),
		lateTransactionIds: pageOf(lateCompletions.transactionIds, latePage),
		stuckTransactionIds: pageOf(stuckTransactionIds, stuckPage),
	};

	const { data, isFetching } = useGetReconcilliationTransactionDetailsQuery(request, {
		skip: Object.values(request).every(ids => ids.length === 0),
	});

	return (
		<div className={styles.sections}>
			<ExceptionTransactionsTable
				title="Refunded After Delivery"
				summary={`${refundedAfterDelivery.amount.toLocaleString()} - ${refundedAfterDelivery.count} Transactions - Known Cost`}
				transactionIds={refundedAfterDelivery.transactionIds}
				rows={data?.refundedAfterDelivery ?? []}
				columns={refundedColumns}
				emptyDescription="No refunds were issued against a delivered vend on this date."
				page={refundedPage}
				onPageChange={setRefundedPage}
				isLoading={isFetching}
			/>

			<ExceptionTransactionsTable
				title="Late Completions"
				summary={`${lateCompletions.count} Transactions - ${lateCompletions.amount.toLocaleString()} - Avg ${lateCompletions.averageDays.toFixed(1)} Days`}
				transactionIds={lateCompletions.transactionIds}
				rows={data?.lateCompletions ?? []}
				columns={lateCompletionColumns}
				emptyDescription="Every vend delivered on this date was bought the same day."
				page={latePage}
				onPageChange={setLatePage}
				isLoading={isFetching}
			/>

			<ExceptionTransactionsTable
				title="Stuck Backlog - 4+ Days Unfulfilled"
				summary={`${stuckTransactionIds.length} Transactions - ${result.backlogAging.fourPlusDays.toLocaleString()}`}
				transactionIds={stuckTransactionIds}
				rows={data?.stuckBacklog ?? []}
				columns={stuckBacklogColumns(result.date)}
				emptyDescription="Nothing paid for on or before this date was still unfulfilled after 4 days."
				page={stuckPage}
				onPageChange={setStuckPage}
				isLoading={isFetching}
			/>
		</div>
	);
};
